import type { ChartConfig } from '@/shared/components/ui/chart'

import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

export type SalesFactorImpact = { metric: string; value: number | null }

const config = {
	value: { label: 'Impact', color: 'var(--primary)' },
} satisfies ChartConfig

/** Column chart of AI importance (%) per sales factor metric. */
export const SalesFactorsImpactChart = ({
	data,
	height = 320,
	'data-testid': testId,
}: {
	data: SalesFactorImpact[]
	height?: number
	'data-testid'?: string
}) => {
	const { t } = useTranslation()
	const label = (metric: string) => t(`metric.${metric}`)

	return (
		<ChartContainer
			config={config}
			className='aspect-auto w-full'
			style={{ height }}
			data-testid={testId}
		>
			<BarChart accessibilityLayer={false} data={data} margin={{ top: 8 }}>
				<CartesianGrid vertical={false} strokeDasharray='3 3' />
				<XAxis
					dataKey='metric'
					tickFormatter={label}
					interval={0}
					tickLine={false}
					axisLine={false}
					fontSize={10}
				/>
				<YAxis
					tickFormatter={(value) => `${value}%`}
					tickLine={false}
					axisLine={false}
					fontSize={10}
					width={36}
				/>
				<ChartTooltip
					cursor={{ fill: 'var(--muted)', fillOpacity: 0.4 }}
					content={
						<ChartTooltipContent
							hideLabel
							className='w-64'
							formatter={(value, _name, item) => {
								const metric = (item.payload as SalesFactorImpact).metric

								return (
									<div className='flex w-full flex-col gap-1'>
										<div className='flex items-center justify-between gap-2'>
											<span className='flex items-center gap-1.5'>
												<span
													className='size-2.5 shrink-0 rounded-[2px]'
													style={{ background: item.color }}
												/>
												{label(metric)}
											</span>
											<span className='font-mono font-medium tabular-nums'>
												{formatNumber(Number(value), NumberFormats.Percent)}
											</span>
										</div>
										<span className='whitespace-normal text-background/70'>
											{t(`metric.${metric}.desc`)}
										</span>
									</div>
								)
							}}
						/>
					}
				/>
				<Bar
					dataKey='value'
					fill='var(--color-value)'
					radius={[4, 4, 0, 0]}
					isAnimationActive={false}
				/>
			</BarChart>
		</ChartContainer>
	)
}
